import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Flatten Zod errors into a simple { field: message } map for inline display. */
export type FieldErrors = Record<string, string>;

export function flattenErrors(result: z.ZodSafeParseError<unknown>): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_root';
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required'),
});

// ---------------------------------------------------------------------------
// User CRUD
// ---------------------------------------------------------------------------
const VALID_ROLES = ['user', 'reviewer', 'admin', 'super_admin'] as const;

export const createUserSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  display_name: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be 100 characters or fewer'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters'),
  role: z.enum(VALID_ROLES, { error: 'Select a valid role' }),
  org_id: z.string().optional(),
  is_active: z.boolean().optional(),
});

export const updateUserSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  display_name: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be 100 characters or fewer'),
  password: z
    .string()
    .refine((v) => v === '' || v.length >= 8, {
      message: 'Password must be at least 8 characters (or leave blank to keep current)',
    }),
  role: z.enum(VALID_ROLES, { error: 'Select a valid role' }),
  org_id: z.string().optional(),
  is_active: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Organisation CRUD
// ---------------------------------------------------------------------------
const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const orgSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or fewer'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .max(50, 'Slug must be 50 characters or fewer')
    .regex(slugRegex, 'Slug must be lowercase letters, numbers and hyphens only (e.g. klm-group)'),
});

/** When editing, slug is immutable — relax its validation. */
export const orgUpdateSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or fewer'),
  slug: z.string(), // kept for payload, not validated
});

// ---------------------------------------------------------------------------
// Category
// ---------------------------------------------------------------------------
const hexColorRegex = /^#[0-9a-fA-F]{6}$/;

export const categorySchema = z.object({
  name: z
    .string()
    .min(1, 'Category name is required')
    .max(80, 'Name must be 80 characters or fewer'),
  description: z.string().max(250, 'Description must be 250 characters or fewer').optional(),
  icon: z.string().min(1, 'Icon is required'),
  color: z
    .string()
    .regex(hexColorRegex, 'Enter a valid hex colour (e.g. #6b7280)'),
  keywords: z.string().optional(),
  llm_instructions: z.string().max(2000, 'Instructions must be 2,000 characters or fewer').optional(),
});

// ---------------------------------------------------------------------------
// ROI Config
// ---------------------------------------------------------------------------
export const roiConfigSchema = z.object({
  daily_call_volume: z
    .number({ error: 'Must be a number' })
    .int({ error: 'Must be a whole number' })
    .min(0, 'Cannot be negative'),
  cost_per_call: z
    .number({ error: 'Must be a number' })
    .min(0, 'Cannot be negative'),
  call_spike_pct: z
    .number({ error: 'Must be a number' })
    .min(0, 'Cannot be negative')
    .max(100, 'Cannot exceed 100%'),
  deflection_rate: z
    .number({ error: 'Must be a number' })
    .min(0, 'Cannot be negative')
    .max(100, 'Cannot exceed 100%'),
  enabled: z.boolean(),
});

// ---------------------------------------------------------------------------
// Profile — display name
// ---------------------------------------------------------------------------
export const profileSchema = z.object({
  display_name: z
    .string()
    .min(1, 'Display name cannot be empty')
    .max(100, 'Display name must be 100 characters or fewer'),
});

// ---------------------------------------------------------------------------
// Change Password
// ---------------------------------------------------------------------------
export const changePasswordSchema = z.object({
  current_password: z
    .string()
    .min(1, 'Enter your current password'),
  new_password: z
    .string()
    .min(8, 'New password must be at least 8 characters'),
  confirm_password: z
    .string()
    .min(1, 'Confirm your new password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'New passwords do not match',
  path: ['confirm_password'],
}).refine((data) => data.current_password !== data.new_password, {
  message: 'New password must be different from current password',
  path: ['new_password'],
});
